// src/context/CartContext.jsx
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'sohocard.cart';

/* --------------------------------------------------------------
   Cart item shape (stored in localStorage):

   {
     productId,      // UUID
     sku,            // e.g. 'apple-25-us'
     title,          // human name
     kind,           // 'gift_card' | 'virtual_card' | 'wallet_topup'
     currency,       // 'USD' | 'EUR' | 'IRT' | …
     unitPrice,      // number
     qty,            // 1..10
     region,         // 'us' | 'eu' | 'global' | 'ir' | null
     brandName,      // 'Apple'
     brandColor,     // '#000000'
     brandLogo,      // full public URL or null
     affiliateUrl,   // full URL or null (only for wallet_topup)
   }
-------------------------------------------------------------- */

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            // Sanity check: ensure it's an array
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    });

    /* Persist on change */
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch {
            /* ignore quota errors */
        }
    }, [items]);

    /* ---------------- add ---------------- */
    /**
     * Add a product to the cart.
     * Accepts either a flat product object (as returned by marketService) or a
     * normalized object from CoinDetails.
     *
     * Every field is optional except productId — sensible defaults are filled in.
     */
    const add = useCallback((product, qty = 1) => {
        if (!product) return;

        const productId = product.id ?? product.productId;
        if (!productId) {
            // eslint-disable-next-line no-console
            console.warn('[CartContext.add] missing product id');
            return;
        }

        const safeQty = Math.max(1, Math.min(10, Number(qty) || 1));

        setItems((prev) => {
            const existing = prev.find((x) => x.productId === productId);

            if (existing) {
                return prev.map((x) =>
                    x.productId === productId
                        ? { ...x, qty: Math.min(x.qty + safeQty, 10) }
                        : x
                );
            }

            const nextItem = {
                productId,
                sku: product.sku || '',
                title: product.title || '',
                kind: product.kind || 'gift_card',
                currency: product.currency || 'USD',
                unitPrice: Number(product.price ?? product.unitPrice ?? 0),
                qty: safeQty,
                region: product.region || null,

                // Brand info — prefer a nested `brands` object, fall back to flat fields
                brandName: product.brands?.name || product.brandName || null,
                brandColor: product.brands?.bg_color || product.brandColor || null,
                brandLogo: product.brands?.logo_url || product.brandLogo || null,

                // Affiliate (wallet_topup)
                affiliateUrl: product.affiliate_url || product.affiliateUrl || null,
            };

            return [...prev, nextItem];
        });
    }, []);

    /* ---------------- remove ---------------- */
    const remove = useCallback((productId) => {
        setItems((prev) => prev.filter((x) => x.productId !== productId));
    }, []);

    /* ---------------- setQty ---------------- */
    const setQty = useCallback((productId, qty) => {
        const safe = Math.max(1, Math.min(10, Number(qty) || 1));
        setItems((prev) =>
            prev.map((x) =>
                x.productId === productId ? { ...x, qty: safe } : x
            )
        );
    }, []);

    /* ---------------- increment / decrement ---------------- */
    const increment = useCallback((productId, by = 1) => {
        setItems((prev) =>
            prev.map((x) =>
                x.productId === productId
                    ? { ...x, qty: Math.min(10, x.qty + by) }
                    : x
            )
        );
    }, []);

    const decrement = useCallback((productId, by = 1) => {
        setItems((prev) =>
            prev.map((x) =>
                x.productId === productId
                    ? { ...x, qty: Math.max(1, x.qty - by) }
                    : x
            )
        );
    }, []);

    /* ---------------- clear ---------------- */
    const clear = useCallback(() => setItems([]), []);

    /* ---------------- derived totals ---------------- */
    const totals = useMemo(() => {
        const subtotal = items.reduce(
            (s, x) => s + Number(x.unitPrice || 0) * Number(x.qty || 0),
            0
        );
        const itemCount = items.reduce((s, x) => s + Number(x.qty || 0), 0);
        const uniqueCount = items.length;
        const hasAffiliate = items.some((x) => !!x.affiliateUrl);

        return { subtotal, itemCount, uniqueCount, hasAffiliate };
    }, [items]);

    /* ---------------- helpers ---------------- */
    const has = useCallback(
        (productId) => items.some((x) => x.productId === productId),
        [items]
    );

    const getItem = useCallback(
        (productId) => items.find((x) => x.productId === productId) || null,
        [items]
    );

    const value = {
        items,
        totals,
        // Convenience aliases
        count: totals.itemCount,
        subtotal: totals.subtotal,
        // Actions
        add,
        remove,
        setQty,
        increment,
        decrement,
        clear,
        // Helpers
        has,
        getItem,
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
    return ctx;
}